import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden grain scratched-black"
        >
            {/* Ambient gold glows for depth — no photographic imagery */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(900px_500px_at_15%_25%,rgba(201,162,39,0.20),transparent_60%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(700px_400px_at_85%_85%,rgba(201,162,39,0.10),transparent_60%)]" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink" />
            </div>

            {/* Vertical hairline left rail */}
            <div className="hidden md:block absolute top-0 bottom-0 left-10 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent" />

            {/* Faint typographic backdrop word */}
            <div
                aria-hidden
                className="hidden lg:block absolute right-[-2vw] top-1/2 -translate-y-1/2 font-display italic text-[22vw] leading-none text-gold/[0.035] tracking-tighter select-none pointer-events-none"
            >
                Ohio.
            </div>

            <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 pt-40 pb-24 md:pt-48 md:pb-32">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="flex items-center gap-3 mb-8"
                >
                    <span className="h-px w-12 bg-gold" />
                    <span
                        data-testid="hero-eyebrow"
                        className="text-xs md:text-sm uppercase tracking-[0.4em] text-gold font-semibold"
                    >
                        Ohio · Built for the Midwest
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                    data-testid="hero-headline"
                    className="font-display text-[3.2rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight max-w-5xl"
                >
                    Politics isn't won{" "}
                    <span className="italic text-cream/70">from behind a desk.</span>
                    <br />
                    It's won{" "}
                    <span className="gold-gradient-text">at the doors.</span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    data-testid="hero-sub"
                    className="mt-10 max-w-2xl text-base md:text-lg text-cream/75 leading-relaxed"
                >
                    Nicholas Kraus Consulting builds winning voter contact programs,
                    recruits volunteers, and turns grassroots support into votes — for
                    central committee races, school boards, county commissioner
                    contests, the statehouse, and statewide tickets across the
                    Midwest.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="mt-12 flex flex-wrap items-center gap-5"
                >
                    <a
                        href="#contact"
                        data-testid="hero-primary-cta"
                        className="group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 uppercase tracking-[0.18em] text-xs font-bold hover:bg-gold-light transition-colors"
                    >
                        Build Your Winning Program
                        <ArrowRight
                            size={16}
                            className="transition-transform group-hover:translate-x-1"
                        />
                    </a>
                    <a
                        href="#services"
                        data-testid="hero-secondary-cta"
                        className="inline-flex items-center gap-2 text-cream/80 hover:text-gold transition-colors uppercase tracking-[0.2em] text-xs font-semibold border-b border-cream/30 hover:border-gold pb-1"
                    >
                        Explore Services
                    </a>
                </motion.div>

                {/* Stat strip — output metrics */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.7 }}
                    className="mt-20 md:mt-28 grid grid-cols-2 lg:grid-cols-4 gap-px bg-gold/20 border border-gold/20 max-w-5xl"
                    data-testid="hero-stat-strip"
                >
                    {[
                        { k: "Tens of Thousands", v: "Doors Knocked" },
                        { k: "Hundreds of Thousands", v: "Phone Calls Made" },
                        { k: "Millions", v: "Text Messages Sent" },
                        { k: "Est. Jan 2026", v: "Ohio-Based, Field-First" },
                    ].map((s, i) => (
                        <div
                            key={i}
                            className="bg-ink/95 px-5 py-5 flex flex-col gap-1"
                        >
                            <div className="font-display text-2xl md:text-3xl text-gold tracking-tight leading-tight">
                                {s.k}
                            </div>
                            <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-cream/60">
                                {s.v}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>

            {/* corner location pin */}
            <div className="absolute bottom-6 right-6 hidden md:flex items-center gap-2 text-cream/40 text-[10px] uppercase tracking-[0.3em]">
                <MapPin size={12} className="text-gold" /> Headquartered in Ohio
            </div>
        </section>
    );
}
